import { useState, useRef, useEffect, useCallback } from 'react';
import { useWakeLock } from '../hooks/useWakeLock';
import './TimerStopwatch.css';

// Format milliseconds as MM:SS:hh (minutes:seconds:hundredths)
function formatTime(ms) {
  if (ms < 0) ms = 0;
  const totalHundredths = Math.floor(ms / 10);
  const hh = totalHundredths % 100;
  const totalSeconds = Math.floor(ms / 1000);
  const ss = totalSeconds % 60;
  const mm = Math.floor(totalSeconds / 60);
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}:${String(hh).padStart(2, '0')}`;
}

// Format delta: +MM:SS:hh or -MM:SS:hh
function formatDelta(ms) {
  const sign = ms >= 0 ? '+' : '-';
  return sign + formatTime(Math.abs(ms));
}

export default function StopwatchPage() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);          // total ms
  const [laps, setLaps] = useState([]);               // lap durations in ms
  const [accumulatedMs, setAccumulatedMs] = useState(0);

  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const accumulatedRef = useRef(0);  // mirror for interval closure
  const runningRef = useRef(false);

  useWakeLock(running);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  // Sync accumulatedRef when state changes
  useEffect(() => {
    accumulatedRef.current = accumulatedMs;
  }, [accumulatedMs]);

  // Sync runningRef when running state changes
  useEffect(() => { runningRef.current = running; }, [running]);

  const tick = useCallback(() => {
    setElapsed(accumulatedRef.current + (Date.now() - startTimeRef.current));
  }, []);

  // Page Visibility API: re-sync on tab return
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && startTimeRef.current) {
        setElapsed(accumulatedRef.current + (Date.now() - startTimeRef.current));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const handleStart = useCallback(() => {
    startTimeRef.current = Date.now();
    intervalRef.current = setInterval(tick, 100);
    setRunning(true);
  }, [tick]);

  const handleStop = useCallback(() => {
    clearInterval(intervalRef.current);
    const now = Date.now();
    const additionalMs = now - startTimeRef.current;
    setAccumulatedMs(prev => {
      const newVal = prev + additionalMs;
      accumulatedRef.current = newVal;
      return newVal;
    });
    startTimeRef.current = null;
    setRunning(false);
  }, []);

  const handleLap = useCallback(() => {
    if (!runningRef.current || !startTimeRef.current) return;
    const currentElapsed = accumulatedRef.current + (Date.now() - startTimeRef.current);
    setLaps(prev => {
      const previousTotal = prev.reduce((sum, l) => sum + l, 0);
      const lapTime = currentElapsed - previousTotal;
      return [...prev, lapTime];
    });
  }, []); // no dependencies needed — uses only refs and functional setState

  const handleReset = useCallback(() => {
    setElapsed(0);
    setLaps([]);
    setAccumulatedMs(0);
    accumulatedRef.current = 0;
    startTimeRef.current = null;
  }, []);

  // Lap analysis: fastest/slowest only when >= 2 laps
  const fastestIdx = laps.length >= 2 ? laps.indexOf(Math.min(...laps)) : null;
  const slowestIdx = laps.length >= 2 ? laps.indexOf(Math.max(...laps)) : null;
  const fastestTime = fastestIdx !== null ? laps[fastestIdx] : null;

  // Reversed lap list for display (newest first)
  const reversedLaps = [...laps].reverse();
  const reversedOffset = laps.length - 1; // to map reversed index → original index

  return (
    <div className="page-content">
      <h4 className="mb-4" style={{ color: 'var(--color-text)', fontWeight: 700 }}>Stoppuhr</h4>

      {/* Sobald die Stoppuhr läuft oder Zeit angesammelt hat, bleibt die Karte
          dunkel; der Leerlauf folgt dem App-Modus. */}
      <div
        className="tools-card"
        data-theme={running || elapsed > 0 ? 'dark' : undefined}
        data-bs-theme={running || elapsed > 0 ? 'dark' : undefined}
      >
        {/* Main time display */}
        <div className="time-display">{formatTime(elapsed)}</div>

        {/* Controls */}
        <div className="tools-btn-row">
          {!running ? (
            <button className="tools-btn-primary" onClick={handleStart}>
              {elapsed === 0 ? 'Start' : 'Weiter'}
            </button>
          ) : (
            <button className="tools-btn-primary" onClick={handleStop}>
              Stop
            </button>
          )}
          <button
            className="tools-btn-ghost"
            onClick={handleLap}
            disabled={!running}
          >
            Runde
          </button>
          <button
            className="tools-btn-ghost"
            onClick={handleReset}
            disabled={running}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Lap list */}
      {laps.length > 0 && (
        <div className="tools-card">
          <div className="timer-history-title">Runden ({laps.length})</div>
          <div className="lap-list">
            {reversedLaps.map((lapMs, reversedIdx) => {
              const originalIdx = reversedOffset - reversedIdx;
              const isFastest = originalIdx === fastestIdx;
              const isSlowest = originalIdx === slowestIdx;
              const delta = fastestTime !== null ? lapMs - fastestTime : null;

              return (
                <div
                  key={originalIdx}
                  className={`lap-row${isFastest ? ' lap-fastest' : ''}${isSlowest ? ' lap-slowest' : ''}`}
                >
                  <span className="lap-number">
                    #{originalIdx + 1}
                  </span>
                  <span className="lap-time">{formatTime(lapMs)}</span>
                  <span className="lap-delta">
                    {delta !== null && !isFastest ? formatDelta(delta) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
